import React, { useState, useRef, useEffect } from 'react';
import '../style/card.css';

const ProjectCard = ({ project, isActive }) => {
  const [showVideo, setShowVideo] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef(null);
  const progressRef = useRef(null);

  // Auto-play when card becomes active
  useEffect(() => {
    if (isActive && videoRef.current) {
      // Reset states for new video
      setShowVideo(false);
      setIsPlaying(false);
      setCurrentTime(0);
      
      // Short delay then play
      const timer = setTimeout(() => {
        setShowVideo(true);
        videoRef.current.play()
          .then(() => {
            setIsPlaying(true);
            setIsBuffering(false);
          })
          .catch((error) => {
            console.log("Autoplay prevented:", error);
            setIsPlaying(false);
          });
      }, 500); // Reduced from 1000ms for quicker response
      
      return () => {
        clearTimeout(timer);
        if (videoRef.current) {
          videoRef.current.pause();
        }
      };
    } else {
      setShowVideo(false);
      setIsPlaying(false);
      setCurrentTime(0);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isActive, project.id]); // Added project.id dependency

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play()
          .then(() => {
            setIsPlaying(true);
            setShowVideo(true);
          })
          .catch((error) => {
            console.log("Play failed:", error);
          });
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleProgressClick = (e) => {
    if (videoRef.current && duration) {
      const rect = progressRef.current.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * duration;
      setCurrentTime(pos * duration);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time) || !isFinite(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // When video ends, stay in ended state
  const handleVideoEnded = () => {
    setIsPlaying(false);
    setCurrentTime(duration);
  };

  return (
    <div className={`project-card ${isActive ? 'active' : 'inactive'}`}>
      {/* Video Container */}
      <div className="project-video-container">
        {/* Thumbnail */}
        <img 
          src={project.thumbnail}
          alt={project.title}
          className={`project-thumbnail ${showVideo ? 'hidden' : ''}`}
        />

        {/* Video */}
        <video
          ref={videoRef}
          src={project.video}
          muted
          loop={false}
          playsInline
          preload="auto"
          className={`project-video ${showVideo ? 'visible' : ''}`}
          onWaiting={() => setIsBuffering(true)}
          onPlaying={() => {
            setIsBuffering(false);
            setIsPlaying(true);
            setShowVideo(true);
          }}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleVideoEnded}
        />

        {/* Video Controls */}
        <div className="project-video-controls">
          {/* Progress Bar */}
          <div 
            ref={progressRef}
            className="project-progress-container"
            onClick={handleProgressClick}
          >
            <div 
              className="project-progress-bar" 
              style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
            >
              <div className="project-progress-handle"></div>
            </div>
          </div>

          {/* Time Display */}
          <div className="project-time-display">
            <span className="project-time-current">{formatTime(currentTime)}</span>
            <button className="project-play-btn" onClick={togglePlay}>
              {isPlaying ? '❚❚' : '►'}
            </button>
            <span className="project-time-duration">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Buffering Indicator */}
        {isBuffering && showVideo && (
          <div className="project-buffering">
            Loading...
          </div>
        )}

        {/* Play Button Overlay (shows when video is not playing) */}
        {!showVideo && (
          <div className="project-play-overlay" onClick={togglePlay}>
            <div className="project-play-icon" />
          </div>
        )}
      </div>

      {/* Title Section */}
      <div className="project-title-section">
        <h3 className="project-title">{project.title}</h3>
      </div>
    </div>
  );
};

export default ProjectCard;